import { useState } from "react";
import type { DragEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DocIcon, FolderIcon, FolderTrashIcon } from "./icons";

// One folder row in the left pane: click to open (its contents show on the
// right), double-click the name to rename, trash to delete, and it is a drop
// target for documents dragged from elsewhere.
export function FolderRow({
  idPrefix,
  name,
  count,
  isOpen,
  isDropTarget,
  onOpen,
  onRename,
  onDelete,
  contentsLoading = false,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  idPrefix?: string;
  name: string;
  count: number;
  isOpen: boolean;
  isDropTarget: boolean;
  onOpen: () => void;
  onRename: (name: string) => void;
  // Only provided in edit mode - without it the trash affordance is hidden.
  // "keep" deletes only the folder (files fall back to "Unsortiert"),
  // "withFiles" deletes the contained documents too. Only set in edit mode.
  onDelete?: (mode: "keep" | "withFiles") => void;
  // "withFiles" deletes only the documents loaded so far; the rest would land in "Unsortiert".
  contentsLoading?: boolean;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
}) {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(name);
  const [confirmDel, setConfirmDel] = useState(false);

  const commit = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== name) onRename(trimmed);
    setEditing(false);
  };

  return (
    <div
      id={idPrefix}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`flex items-center gap-2 px-3 py-2 transition ${
        isDropTarget
          ? "box-flat bg-ink/[0.06] smycles-drop-target ring-2 ring-accent-decoration"
          : isOpen
            ? // Header of an expanded folder: no own frame - the wrapper's
              // border encloses header + files as one unit.
              "border-b border-ink/10 bg-ink/[0.06]"
            : "box-flat bg-page hover:bg-ink/[0.06]"
      }`}
    >
      {editing ? (
        <>
          <span
            className={`shrink-0 transition-transform ${isOpen ? "rotate-90 text-ink-accent" : "text-ink"}`}
          >
            <DocIcon>
              <path d="m9 6 6 6-6 6" />
            </DocIcon>
          </span>
          <span className={isOpen ? "text-ink-accent" : "text-ink"}>
            <FolderIcon />
          </span>
          <input
            id={idPrefix && `${idPrefix}-rename`}
            autoFocus
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              else if (e.key === "Escape") {
                setEditName(name);
                setEditing(false);
              }
            }}
            aria-label={intl.formatMessage({ id: "service.folderRow.renameFolder", defaultMessage: "Rename folder" })}
            className="smycles-field-meta min-w-0 flex-1 rounded-md bg-surface px-2 py-1 font-medium ring-1 ring-accent-decoration focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
          />
        </>
      ) : (
        // Chevron + folder icon live INSIDE the toggle button, so tapping
        // them (or anywhere up to the name) collapses/expands too.
        <button
          id={idPrefix && `${idPrefix}-toggle`}
          type="button"
          onClick={onOpen}
          onDoubleClick={() => {
            setEditName(name);
            setEditing(true);
          }}
          title={intl.formatMessage({ id: "service.folderRow.toggleHint", defaultMessage: "Expand/collapse (double-click: rename)" })}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span
            className={`shrink-0 transition-transform ${isOpen ? "rotate-90 text-ink-accent" : "text-ink"}`}
          >
            <DocIcon>
              <path d="m9 6 6 6-6 6" />
            </DocIcon>
          </span>
          <span className={isOpen ? "text-ink-accent" : "text-ink"}>
            <FolderIcon />
          </span>
          <span
            id={idPrefix && `${idPrefix}-name`}
            className="smycles-field-meta min-w-0 flex-1 truncate font-medium hover:text-ink-accent"
          >
            {name}
          </span>
        </button>
      )}
      <span className="smycles-field-label shrink-0 text-ink-muted">{count}</span>
      {onDelete ? (
        confirmDel ? (
          <span className="flex shrink-0 items-center gap-1.5 text-smycles-micro">
            {count > 0 ? (
              // Folder still holds files → let the user decide their fate.
              <>
                <span className="text-ink">
                  <FormattedMessage id="service.folderRow.deleteLabel" defaultMessage="Delete:" />
                </span>
                <button
                  id={idPrefix && `${idPrefix}-delete-keep`}
                  type="button"
                  onClick={() => {
                    setConfirmDel(false);
                    onDelete("keep");
                  }}
                  title={intl.formatMessage(
                    {
                      id: "service.folderRow.deleteFolderOnlyHint",
                      defaultMessage: "Delete only the folder: {count, plural, one {# file moves} other {# files move}} to “Unsorted”",
                    },
                    { count },
                  )}
                  className="font-medium text-ink hover:underline"
                >
                  <FormattedMessage id="service.folderRow.folderOnly" defaultMessage="Folder only" />
                </button>
                <button
                  id={idPrefix && `${idPrefix}-delete-withfiles`}
                  type="button"
                  disabled={contentsLoading}
                  onClick={() => {
                    setConfirmDel(false);
                    onDelete("withFiles");
                  }}
                  title={contentsLoading
                    ? intl.formatMessage({
                        id: "service.folderRow.withFilesLoadingHint",
                        defaultMessage: "Available once all documents have loaded",
                      })
                    : intl.formatMessage(
                        {
                          id: "service.folderRow.deleteWithFilesHint",
                          defaultMessage: "Permanently delete the folder along with {count, plural, one {# file} other {# files}}",
                        },
                        { count },
                      )}
                  className="font-medium text-red-600 hover:underline disabled:no-underline disabled:opacity-40"
                >
                  <FormattedMessage id="service.folderRow.withFiles" defaultMessage="With files" />
                </button>
              </>
            ) : (
              <button
                id={idPrefix && `${idPrefix}-delete-confirm`}
                type="button"
                onClick={() => {
                  setConfirmDel(false);
                  onDelete("keep");
                }}
                className="font-medium text-red-600 hover:underline"
              >
                <FormattedMessage id="common.delete" defaultMessage="Delete" />
              </button>
            )}
            <button
              id={idPrefix && `${idPrefix}-delete-cancel`}
              type="button"
              onClick={() => setConfirmDel(false)}
              className="text-ink hover:text-ink"
            >
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </button>
          </span>
        ) : (
          <button
            id={idPrefix && `${idPrefix}-delete`}
            type="button"
            onClick={() => setConfirmDel(true)}
            aria-label={intl.formatMessage(
              { id: "service.folderRow.deleteFolderAria", defaultMessage: "Delete folder {name}" },
              { name },
            )}
            title={intl.formatMessage({ id: "service.folderRow.deleteFolder", defaultMessage: "Delete folder" })}
            className="shrink-0 text-ink-muted transition hover:text-red-600"
          >
            <FolderTrashIcon />
          </button>
        )
      ) : null}
    </div>
  );
}

export function NewFolderRow(x: {
  idPrefix?: string;
  onCreate: (name: string) => Promise<boolean>;
  error?: string | null;
}) {
  const intl = useIntl();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");

  const commit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setName("");
      setAdding(false);
      return;
    }
    const ok = await x.onCreate(trimmed);
    if (ok) {
      setName("");
      setAdding(false);
    }
    // On failure keep the field open; the parent-supplied `error` shows below it.
  };

  const errorLine = x.error ? (
    <div id={x.idPrefix && `${x.idPrefix}-error`} data-testid="folder-error" className="mt-1 px-1 text-smycles-label text-red-600">
      {x.error}
    </div>
  ) : null;

  if (!adding) {
    return (
      <div>
        <button
          id={x.idPrefix}
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-2 rounded-md px-3 py-2 text-smycles-meta text-ink-muted ring-1 ring-ink/15 transition hover:text-ink-accent hover:ring-accent-decoration/50"
        >
          <span>+</span>{" "}
          <FormattedMessage id="service.folderRow.newFolder" defaultMessage="New folder" />
        </button>
        {errorLine}
      </div>
    );
  }
  return (
    <div>
      <input
        id={x.idPrefix && `${x.idPrefix}-name`}
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          else if (e.key === "Escape") {
            setName("");
            setAdding(false);
          }
        }}
        placeholder={intl.formatMessage({ id: "service.folderRow.folderNamePlaceholder", defaultMessage: "Folder name …" })}
        className={`smycles-field-meta rounded-md bg-surface px-3 py-2 ring-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui placeholder:text-ink-muted ${x.error ? "ring-red-500" : "ring-accent-decoration"}`}
      />
      {errorLine}
    </div>
  );
}
